import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Menu,
  notification,
  Popover,
  Spin,
} from "antd";
import { useMap } from "react-leaflet";
import styled from "styled-components";
import { useMemo, useState } from "react";
import { Typography } from "antd";
import {
  BrowserUtility,
  DefaultMapCenter,
  StorageConstant,
  CommonUtility,
  CustomLayerService,
  DateUtility,
} from "../../utility";
import {
  CaretRight,
  Export,
  FunnelSimple,
  Plus,
  Calendar,
  Trash,
} from "@phosphor-icons/react";
import { SearchInputLayers } from "../../hooks/search";
import { LayerUploadModal } from "./LayerUploadModal";
import { LayerFilterModal } from "./LayerFilterModal";
import { FlexBox } from "../../components";
import { useLoading } from "../../hooks/loadingContext";
import { CustomLayerModal } from "./CustomeLayerModal";

const { Title } = Typography;

const CustomMenu = styled(Menu)`
  border-inline-end: 0px !important;
  &.ant-menu {
  }
  li {
    div {
    }
  }
`;

const LogoHeader = styled.div`
  display: flex;
  align-items: center;
  background: white !important;
  gap: 8px;
  img {
    width: 40px;
  }
  h2 {
    margin: 0px !important;
  }
`;

const MapSider = styled.div`
  background: white !important;
  .ant-layout-sider-trigger {
    background: white !important;
    display: none;
  }
  .ant-menu-item {
    height: auto !important;
    text-wrap: wrap;
  }
  height: 100%;
  position: relative;
`;

const SideBarBox = styled.div`
  position: absolute;
  top: 100px;
  left: 0;
  z-index: 990;

  button {
    height: auto;
  }
`;

const UploadButton = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Scroller = styled.div`
  height: calc(100vh - 180px);
  overflow: scroll;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

// const MenuContainer = styled(FlexRow)`
/* justify-content: space-between; */
// `;

const MenuContainer = styled.div``;

const LayerDatePicker = ({ item, selectedDates, handleDateChange }) => {
  return (
    <DatePicker.RangePicker
      className="col-12"
      value={
        selectedDates[item.key]
          ? [
              DateUtility.toDayJs(selectedDates[item.key][0]),
              DateUtility.toDayJs(selectedDates[item.key][1]),
            ]
          : []
      }
      minDate={DateUtility.toDayJs(new Date(item.dateRange[0]))}
      maxDate={DateUtility.toDayJs(new Date(item.dateRange[1]))}
      onChange={(date, dateString) =>
        handleDateChange(date, dateString, item.key)
      }
      onClear={() => {
        handleDateChange([], [], item.key);
      }}
    />
  );
};

export const DrawerBox = ({
  filterChanged,
  onLayerChange,
  defaultLayers,
  clearMap,
  onSaveLayer,
  filter,
  refreshData,
  onFilterChange,
}) => {
  const [drawer, setDrawer] = useState(false);
  const [openModel, setOpenModel] = useState(false);
  const [filterModel, setFilterModel] = useState(false);
  const [selectedDates, setSelectedDates] = useState({}); // Store dates per layer
  const [openPopoverKey, setOpenPopoverKey] = useState(null); // Track open Popover

  const [selectedLayer, setSelectedLayer] = useState();
  const [requestLayerKey, setRequestLayerKey] = useState("");
  const [processing, setProcessing] = useState(false);
  const [customRequestModal, setCustomeRequestModal] = useState(false);
  const { loading } = useLoading();
  const map = useMap();
  const onClear = () => {
    map.setView(DefaultMapCenter, 8);
    clearMap();
  };

  const handleDateChange = (dates, dateStrings, layerKey) => {
    setSelectedDates((prevState) => ({
      ...prevState,
      [layerKey]: dateStrings?.filter((ele) => ele), // Save the date range for the specific layer
    }));
    onLayerChange(
      null,
      layerKey,
      "dateFilter",
      dateStrings?.filter((ele) => ele)
    );
  };

  const deleteLayer = async (key) => {
    try {
      setProcessing(true);
      await CustomLayerService.delete(key);
      notification.success({ message: "Success! Layer has been removed." });
      refreshData();
      setProcessing(false);
    } catch (error) {
      notification.error({
        message:
          error?.message ||
          "An unexpected error occurred. Please contact support for assistance.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const saveLayer = () => {
    const mapCenter = map.getCenter();
    if (mapCenter) {
      BrowserUtility.saveObj(StorageConstant.mapCenter, [
        mapCenter.lat,
        mapCenter.lng,
      ]);
    }
    BrowserUtility.save(StorageConstant.zoom, map.getZoom());
    onSaveLayer();
  };

  const { items, defaultOpenKeys } = useMemo(() => {
    const grouped = CommonUtility.groupedData(defaultLayers, "category");
    const defaultOpenKeys = Object.keys(grouped).filter(
      (ele) => grouped[ele].some((ele) => ele.checked) && ele
    );

    const commonlyUsedLayers = [
      "Custom",
      "Electrical Infrastructure",
      "Oil and Gas Infrastructure",
      "Communications",
      "Zones",
    ];
    let commonlyUsed = {};
    let allLayers = {};

    for (let category in grouped) {
      if (commonlyUsedLayers.includes(category)) {
        commonlyUsed[category] = grouped[category]; // Add to "Commonly Used Layers"
      } else {
        allLayers[category] = grouped[category]; // Add to "All Layers"
      }
    }
    const sortedAllLayers = Object.keys(allLayers)
      .sort()
      .reduce((obj, key) => {
        obj[key] = allLayers[key];
        return obj;
      }, {});

    const transformData = (data) => {
      const result = {};

      for (const category in data) {
        result[category] = {};

        data[category].forEach((item) => {
          const subCategory = item.subCategory;

          if (!result[category][subCategory]) {
            result[category][subCategory] = [];
          }

          result[category][subCategory].push(item);
        });
      }

      return result;
    };

    const transformedData = transformData(
      Object.fromEntries(
        commonlyUsedLayers
          .map((key) => [key, commonlyUsed[key] || []])
          .filter(
            ([key, value]) => !(Array.isArray(value) && value.length === 0)
          )
      )
    );

    const onLayerSelected = (e, item) => {
      if (item.dateFilter && item.dateRange.length) {
        if (e.target.checked) {
          setSelectedDates((prevState) => ({
            ...prevState,
            [item.key]: [
              DateUtility.toDayJs(new Date(item.dateRange[0])),
              DateUtility.toDayJs(new Date(item.dateRange[1])),
            ],
          }));
          onLayerChange(null, item.key, "dateFilter", item.dateRange);
        } else {
          setSelectedDates((prevState) => ({
            ...prevState,
            [item.key]: [],
          }));
          onLayerChange(null, item.key, "dateFilter", []);
        }
        return;
      }
      onLayerChange(e, item.key);
    };

    const transformedData1 = transformData(sortedAllLayers);
    const generateMenuItems = (data) => {
      return Object.keys(data).map((categoryKey) => ({
        key: categoryKey,
        label: categoryKey === "Custom" ? "Uploads" : categoryKey,
        children: Object.keys(data[categoryKey]).map((subCategoryKey) => ({
          key: subCategoryKey,
          label: subCategoryKey,
          children: (data[categoryKey][subCategoryKey] || []).map((item) => ({
            key: item.key,
            label: (
              <FlexBox $justify="space-between" $align="center">
                <Checkbox
                  disabled={processing}
                  checked={item.checked}
                  onChange={(e) => onLayerSelected(e, item)}
                >
                  {item.name}
                </Checkbox>
                {item.category === "Custom" && (
                  <div>
                    <Button
                      disabled={processing}
                      icon={<Trash />}
                      className="btn bg-transparent border-0 p-0"
                      onClick={() => deleteLayer(item?.key)}
                    />
                    <Button
                      disabled={processing}
                      icon={<Export />}
                      className="btn bg-transparent border-0 p-0"
                      onClick={(e) => onLayerChange(e, item.key, "isExport")}
                    />
                    <Button
                      disabled={processing}
                      icon={<Plus />}
                      className="btn bg-transparent border-0 p-0"
                      onClick={(e) => {
                        console.log("item", item);
                        setCustomeRequestModal(true);
                        setRequestLayerKey(item.key);
                      }}
                    />
                  </div>
                )}
                {item.filter?.length > 0 && (
                  <Button
                    disabled={processing}
                    className="btn bg-transparent border-0 p-0"
                    icon={<FunnelSimple />}
                    onClick={() => {
                      setSelectedLayer(item);
                      setFilterModel(true);
                    }}
                  />
                )}
                {item?.dateFilter && item?.dateRange?.length && (
                  <Popover
                    content={
                      <LayerDatePicker
                        item={item}
                        selectedDates={selectedDates}
                        handleDateChange={handleDateChange}
                      />
                    }
                    open={openPopoverKey === item.key}
                    trigger="click"
                    destroyTooltipOnHide
                    onOpenChange={(visible) => {
                      if (visible) {
                        setOpenPopoverKey(item.key); // Set the current layer as the one that's open
                      } else {
                        setOpenPopoverKey(null); // Close the popover when clicked outside
                      }
                    }}
                  >
                    <Button
                      disabled={processing}
                      className="btn bg-transparent border-0 p-0"
                      icon={<Calendar />}
                      onClick={() => setOpenPopoverKey(item.key)} // Open the specific Popover
                    />
                  </Popover>
                )}
              </FlexBox>
            ),
          })),
        })),
      }));
    };
    const items = [
      {
        key: "commonly-used-dropdown",
        label: "Commonly Used Layers",
        children: generateMenuItems(transformedData),
      },
      {
        key: "all-layers",
        label: "All Layers",
        children: generateMenuItems(transformedData1),
      },
    ];
    return { items, defaultOpenKeys };
  }, [defaultLayers, onLayerChange, openPopoverKey, selectedDates, processing]);

  const closeMddal = (result) => {
    if (result) {
      refreshData();
    }
    setOpenModel(false);
  };

  const closeCustomLayerModal = (result) => {
    if (result) {
      refreshData();
    }
    setCustomeRequestModal(false);
  };

  return (
    <>
      <Drawer
        placement="left"
        className="StatmentDrawer"
        onClose={() => {
          setDrawer(false);
        }}
        open={drawer}
        title={
          <LogoHeader>
            <div className="d-flex col-12 justify-content-between align-items-center">
              <div className="d-flex  align-items-center">
                <img alt="Juicee logo" src="./juicee-logo.png" />
                &nbsp;&nbsp; &nbsp;&nbsp;<Title level={2}>Juicee</Title>
              </div>{" "}
            </div>
          </LogoHeader>
        }
      >
        <div
          className={`${
            loading ? "d-block" : "d-none"
          } h-100 d-flex justify-content-center align-items-center`}
        >
          <Spin spinning={loading} />
        </div>
        <div className={loading ? "d-none" : "d-block"}>
          <MapSider>
            <Scroller>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <Title level={3}>Map Layers</Title>{" "}
                <Button className="mb-3" onClick={saveLayer}>
                  Save Layers
                </Button>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <SearchInputLayers
                  value={filter.search}
                  onSearch={(search) => filterChanged({ search })}
                />
                <Button className="ms-2" onClick={onClear}>
                  Reset
                </Button>
              </div>
              <CustomMenu
                mode="inline"
                items={items.filter(
                  (item) => item.children && item.children.length > 0
                )}
                defaultOpenKeys={defaultOpenKeys}
              />
            </Scroller>

            <UploadButton>
              <Button type="primary" block onClick={() => setOpenModel(true)}>
                Upload Layer
              </Button>
            </UploadButton>
          </MapSider>
        </div>
      </Drawer>
      <SideBarBox>
        <Button onClick={() => setDrawer(true)}>
          <CaretRight size={28} />
        </Button>
      </SideBarBox>
      <LayerUploadModal closeModal={closeMddal} open={openModel} />
      <CustomLayerModal
        closeModal={closeCustomLayerModal}
        open={customRequestModal}
        layerKey={requestLayerKey}
      />
      <LayerFilterModal
        onFilterChange={onFilterChange}
        data={selectedLayer}
        closeModal={() => setFilterModel(false)}
        open={filterModel}
      />
    </>
  );
};
